import { z } from "zod";

import { parentRemote } from "./miniappRpc";

interface CachedToken {
  jwt: string;
  expiresAt: number;
}

let cachedToken: CachedToken | null = null;
let inFlightTokenFetch: Promise<string> | null = null;

const TOKEN_REFRESH_THRESHOLD_SECONDS = 60;

const jwtPayloadSchema = z.object({
  exp: z.number(),
});

function isTokenStillFresh(token: CachedToken): boolean {
  const nowSeconds = Math.floor(Date.now() / 1000);
  return token.expiresAt - nowSeconds > TOKEN_REFRESH_THRESHOLD_SECONDS;
}

function getJwtPayload(jwt: string): unknown {
  const [, payload] = jwt.split(".");
  if (!payload) {
    return null;
  }
  try {
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return null;
  }
}

async function requestTokenFromParent(): Promise<string> {
  const result = await parentRemote.requestAuth();
  const expiresAt = jwtPayloadSchema.parse(getJwtPayload(result.token)).exp;
  cachedToken = {
    jwt: result.token,
    expiresAt,
  };
  return result.token;
}

export async function getMiniappToken(opts: {
  forceRefresh?: boolean;
}): Promise<string> {
  if (opts.forceRefresh) {
    cachedToken = null;
  } else if (cachedToken && isTokenStillFresh(cachedToken)) {
    return cachedToken.jwt;
  }
  if (inFlightTokenFetch) {
    return inFlightTokenFetch;
  }
  inFlightTokenFetch = requestTokenFromParent().finally(() => {
    inFlightTokenFetch = null;
  });
  return inFlightTokenFetch;
}
